import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Pencil, Trash2, Globe, Lock, ExternalLink, Paperclip, X, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import AttachmentPickerSheet from '../shared/AttachmentPickerSheet';
import LinkedNoteDialog from './LinkedNoteDialog';
import { useConfirm } from '../../components/common/ConfirmDialog';

const PERFORMED_BY_TYPES = ['In-house', 'Vet', 'Farrier', 'Trainer', 'Other'];

function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function formatDate(d, t) {
  if (!d) return '—';
  const date = new Date(d + 'T12:00:00');
  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return t ? `${label} @ ${fmt12(t)}` : label;
}

export default function CareLogEntryDetailModal({ open, onClose, entry: initialEntry, locations, onDeleted, onUpdated }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [editing, setEditing]   = useState(false);
  const [entry, setEntry]       = useState(null);
  const [form, setForm]         = useState({});
  const [saving, setSaving]     = useState(false);
  const [lockStatus, setLockStatus] = useState({ locked: false, invoice_id: null, invoice_number: null });
  const [attachPickerOpen, setAttachPickerOpen] = useState(false);
  const [pendingFiles, setPendingFiles]         = useState([]);
  const [uploading, setUploading]               = useState(false);
  const [noteDialogOpen, setNoteDialogOpen]     = useState(false);

  const CURRENCIES = ['USD','EUR','GBP','CAD','AUD','NZD','ZAR','CHF','JPY','HKD'];

  useEffect(() => {
    if (open && initialEntry) {
      setEntry(initialEntry);
      setEditing(false);
      setLockStatus({ locked: false, invoice_id: null, invoice_number: null });
      setForm({
        category:             initialEntry.category || '',
        type:                 initialEntry.type || '',
        name:                 initialEntry.name || '',
        dosage:               initialEntry.dosage || '',
        dosage_unit:          initialEntry.dosage_unit || '',
        units:                initialEntry.units ?? '',
        log_date:             initialEntry.log_date || '',
        log_time:             initialEntry.log_time || '',
        location_id:          initialEntry.location_id || '',
        performed_by:         initialEntry.performed_by || '',
        performed_by_type:    initialEntry.performed_by_type || 'In-house',
        comments:             initialEntry.comments || '',
        publish_to_owner_portal: initialEntry.publish_to_owner_portal || false,
        billable:             initialEntry.billable || false,
        cost:                 initialEntry.cost ?? '',
        cost_currency:        initialEntry.cost_currency || 'USD',
        is_breeding:          initialEntry.is_breeding || false,
      });
      // Check invoice lock status
      api.get(`/farmfolio/care-log/${initialEntry.id}/invoice-lock`)
        .then(res => setLockStatus(res.data))
        .catch(() => {});
    }
  }, [open, initialEntry]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/farmfolio/care-log/${entry.id}`, {
        ...form,
        units: form.units !== '' ? parseFloat(form.units) || null : null,
        cost: form.billable && form.cost !== '' ? parseFloat(form.cost) || null : null,
      });
      // Upload pending attachments
      if (pendingFiles.length > 0) {
        setUploading(true);
        await Promise.allSettled(pendingFiles.map(file => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('horse_id', entry.horse_id || '');
          fd.append('description', `Care Log — ${entry.log_date || ''}`);
          return api.post('/files/upload', fd);
        }));
        setPendingFiles([]);
        setUploading(false);
      }
      setEntry(res.data);
      setEditing(false);
      toast.success('Care log entry updated');
      if (onUpdated) onUpdated(res.data);
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!await confirm({ title: 'Delete this care log entry?', destructive: true })) return;
    try {
      await api.delete(`/farmfolio/care-log/${entry.id}`);
      toast.success('Entry deleted');
      if (onDeleted) onDeleted(entry.id);
      onClose();
    } catch { toast.error('Failed to delete'); }
  };

  const handleUnpublish = async () => {
    try {
      const res = await api.put(`/farmfolio/care-log/${entry.id}`, { publish_to_owner_portal: false });
      setEntry(res.data);
      setForm(p => ({ ...p, publish_to_owner_portal: false }));
      toast.success('Unpublished from Owner Portal');
      if (onUpdated) onUpdated(res.data);
    } catch { toast.error('Failed to unpublish'); }
  };

  if (!entry) return null;

  const displayName = entry.name + (entry.dosage
    ? ` (${entry.dosage}${entry.dosage_unit ? ' ' + entry.dosage_unit : ''})`
    : '');

  return (
    <>
    <Dialog open={open} onOpenChange={v => { if (!v) { setEditing(false); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Care Log Entry</DialogTitle>
        </DialogHeader>

        {/* Horse name — shown when coming from global view */}
        {initialEntry?.horse_name && (
          <div className="px-1 pb-1">
            <div className="rounded border px-3 py-2 text-sm bg-muted/20 font-medium text-foreground">
              {initialEntry.horse_name}
            </div>
          </div>
        )}

        <div className="space-y-3 py-2">
          {editing ? (
            /* ── Edit mode ─────────────────────────────────────── */
            <>
              <div className="space-y-1">
                <Label className="font-semibold">Category</Label>
                <div className="rounded border px-3 py-2 text-sm bg-muted/20">{form.category}</div>
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Type</Label>
                <div className="rounded border px-3 py-2 text-sm bg-muted/20">{form.type}</div>
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Name</Label>
                <Input value={form.name} onChange={e => f('name', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Quantity</Label>
                <Input type="number" min="0" step="0.01" value={form.units}
                  onChange={e => f('units', e.target.value)} placeholder="e.g. 1, 2.5" />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Date</Label>
                <Input type="date" value={form.log_date} onChange={e => f('log_date', e.target.value)}
                  className="time-filled" />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Time</Label>
                <Input type="time" value={form.log_time} onChange={e => f('log_time', e.target.value)}
                  className={form.log_time ? 'time-filled' : ''} />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Location</Label>
                <Select value={form.location_id || '__none__'} onValueChange={v => f('location_id', v === '__none__' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">By</Label>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={form.performed_by_type === 'In-house' ? 'text-primary font-medium' : 'text-muted-foreground'}>In-house</span>
                    <Switch checked={form.performed_by_type !== 'In-house'} onCheckedChange={v => f('performed_by_type', v ? 'Other' : 'In-house')} />
                    <span className={form.performed_by_type !== 'In-house' ? 'text-primary font-medium' : 'text-muted-foreground'}>Other</span>
                  </div>
                </div>
                <Input value={form.performed_by} onChange={e => f('performed_by', e.target.value)}
                  placeholder={form.performed_by_type !== 'In-house' ? "Person's name" : "Person's name"} />
              </div>
              <div className="space-y-1">
                <Label className="font-semibold">Comments</Label>
                <Textarea rows={3} value={form.comments} onChange={e => f('comments', e.target.value)} />
              </div>
              {/* Billable / Cost — Travel entries and Bred entries */}
              {(form.category === 'Travel' || form.type === 'Bred') && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Switch checked={form.billable} onCheckedChange={v => f('billable', v)} />
                    <Label className="cursor-pointer font-semibold">Billable</Label>
                  </div>
                  {form.billable && (
                    <div className="space-y-1">
                      <Label className="font-semibold">Cost</Label>
                      <div className="flex gap-2">
                        <Select value={form.cost_currency || 'USD'} onValueChange={v => f('cost_currency', v)}>
                          <SelectTrigger className="w-20 shrink-0"><SelectValue /></SelectTrigger>
                          <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input type="number" min="0" step="0.01" value={form.cost}
                          onChange={e => f('cost', e.target.value)} placeholder="0.00" className="flex-1" />
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Publish — toggle if not published, badge if already published */}
              {entry.publish_to_owner_portal ? (
                <div className="flex items-center justify-between p-2 border border-green-300 rounded bg-green-50 text-xs">
                  <div className="flex items-center gap-1.5 text-green-700">
                    <Globe className="w-3.5 h-3.5" />
                    <span className="font-medium">Published to Owner Portal</span>
                    <span className="text-green-600">· {entry.log_date}{entry.log_time ? ' @ ' + fmt12(entry.log_time) : ''}</span>
                  </div>
                  <Button size="sm" variant="outline" className="h-6 text-xs border-green-400 text-green-700 px-2" onClick={handleUnpublish}>Undo</Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Switch checked={form.publish_to_owner_portal} onCheckedChange={v => f('publish_to_owner_portal', v)} />
                  <Label className="cursor-pointer text-sm">Publish to Owner Portal</Label>
                </div>
              )}
            </>
          ) : (
            /* ── View mode ─────────────────────────────────────── */
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-xs text-muted-foreground font-medium">Category</p><p>{entry.category}</p></div>
                <div><p className="text-xs text-muted-foreground font-medium">Type</p><p>{entry.type}</p></div>
              </div>
              <div><p className="text-xs text-muted-foreground font-medium">Name</p><p>{displayName}</p></div>
              {entry.units != null && (
                <div><p className="text-xs text-muted-foreground font-medium">Quantity</p><p>{entry.units}</p></div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Date</p>
                  <p>{entry.log_date ? new Date(entry.log_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</p>
                </div>
                <div><p className="text-xs text-muted-foreground font-medium">Time</p><p>{entry.log_time ? fmt12(entry.log_time) : '—'}</p></div>
              </div>
              {entry.location_name && (<div><p className="text-xs text-muted-foreground font-medium">Location</p><p>{entry.location_name}</p></div>)}
              {entry.performed_by && (
                <div><p className="text-xs text-muted-foreground font-medium">By</p>
                  <p>{entry.performed_by} <span className="text-muted-foreground text-xs">({entry.performed_by_type})</span></p>
                </div>
              )}
              {entry.comments && (<div><p className="text-xs text-muted-foreground font-medium">Comments</p><p className="whitespace-pre-wrap">{entry.comments}</p></div>)}
              {/* Billable / Cost — Travel entries */}
              {entry.category === 'Travel' && entry.billable && entry.cost != null && (
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Cost</p>
                  <p>{entry.cost_currency || 'USD'} {Number(entry.cost).toFixed(2)}</p>
                </div>
              )}
              {/* Publish status badge */}
              {entry.publish_to_owner_portal && (
                <div className="flex items-center justify-between p-2 border border-green-300 rounded bg-green-50 text-xs">
                  <div className="flex items-center gap-1.5 text-green-700">
                    <Globe className="w-3.5 h-3.5" />
                    <span className="font-medium">Published to Owner Portal</span>
                    <span className="text-green-600">· {entry.log_date}{entry.log_time ? ' @ ' + fmt12(entry.log_time) : ''}{entry.performed_by ? ' · ' + entry.performed_by : ''}</span>
                  </div>
                  <Button size="sm" variant="outline" className="h-6 text-xs border-green-400 text-green-700 px-2" onClick={handleUnpublish}>Undo</Button>
                </div>
              )}
              {/* Invoice lock banner / static note */}
              {lockStatus.locked ? (
                <div className="flex items-start gap-2 p-2.5 border border-amber-300 bg-amber-50 rounded text-xs text-amber-800"
                  data-testid="care-log-invoice-lock-banner">
                  <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="font-semibold">Locked — </span>
                    This entry is included in approved invoice{lockStatus.invoice_number ? ` ${lockStatus.invoice_number}` : ''} and cannot be edited or deleted.
                  </div>
                  {lockStatus.invoice_id && (
                    <button
                      onClick={async () => { onClose(); navigate(`/invoices/${lockStatus.invoice_id}`); }}
                      className="shrink-0 flex items-center gap-1 underline hover:no-underline"
                      data-testid="care-log-view-invoice-link">
                      <ExternalLink className="w-3 h-3" /> View
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic pt-1">Note: editing is disabled once an associated invoice is approved.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer — pencil left (view only), trash + actions right */}
        <div className="flex items-center justify-between pt-2 border-t mt-2">
          <div>
            {!editing && !lockStatus.locked && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(true)} title="Edit"
                data-testid="care-log-edit-btn">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {editing && (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Add attachment"
                  onClick={() => setAttachPickerOpen(true)}>
                  <Paperclip className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Create linked note"
                  onClick={() => setNoteDialogOpen(true)}>
                  <StickyNote className="w-3.5 h-3.5" />
                </Button>
                {pendingFiles.length > 0 && (
                  <span className="text-xs text-muted-foreground">{pendingFiles.length} file(s) pending</span>
                )}
                <Button variant="outline" onClick={async () => { setEditing(false); setPendingFiles([]); }} disabled={saving || uploading}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving || uploading} className="bg-primary hover:bg-primary/90 text-white">
                  {saving || uploading ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
            {!editing && !lockStatus.locked && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleDelete} title="Delete"
                data-testid="care-log-delete-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <AttachmentPickerSheet
      open={attachPickerOpen}
      onClose={() => setAttachPickerOpen(false)}
      onFiles={(files) => setPendingFiles(prev => [...prev, ...files])}
    />
    <LinkedNoteDialog
      open={noteDialogOpen}
      onClose={() => setNoteDialogOpen(false)}
      onSaved={() => setNoteDialogOpen(false)}
      horse={{ id: entry?.horse_id }}
      careEntryRef={`Care Log — ${entry?.log_date || ''}`}
    />
    </>
  );
}